import { ref } from "vue";

// Singleton reactive state across the app
const theme = ref("light"); // 'light' | 'dark' | 'system'
const isDark = ref(false);
let initialized = false;

function applyTheme() {
  if (typeof window === "undefined") return;

  let effectiveDark = false;
  if (theme.value === "dark") {
    effectiveDark = true;
  } else if (theme.value === "light") {
    effectiveDark = false;
  } else {
    // system preference
    effectiveDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  isDark.value = effectiveDark;

  if (effectiveDark) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }

  // Update theme-color meta tag for mobile browser chrome
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      "content",
      effectiveDark ? "#0f172a" : "#4f46e5"
    );
  }

  // Sync AI Chatbot storage if present
  try {
    localStorage.setItem("edubot-dark-mode", JSON.stringify(effectiveDark));
  } catch (e) {
    // Ignore storage errors
  }
}

export function useTheme() {
  if (!initialized && typeof window !== "undefined") {
    initialized = true;

    try {
      const savedTheme = localStorage.getItem("theme");
      if (
        savedTheme === "dark" ||
        savedTheme === "light" ||
        savedTheme === "system"
      ) {
        theme.value = savedTheme;
      } else {
        const oldEdubot = localStorage.getItem("edubot-dark-mode");
        if (oldEdubot === "true") {
          theme.value = "dark";
        } else if (
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
          theme.value = "dark";
        } else {
          theme.value = "light";
        }
      }
    } catch (e) {
      theme.value = "light";
    }

    applyTheme();

    // Listen for OS system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => {
        if (theme.value === "system") {
          applyTheme();
        }
      };
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handler);
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(handler);
      }
    }
  }

  const setTheme = (newTheme) => {
    theme.value = newTheme;
    try {
      localStorage.setItem("theme", newTheme);
    } catch (e) {
      // Ignore storage errors
    }
    applyTheme();
  };

  const toggleTheme = () => {
    if (isDark.value) {
      setTheme("light");
    } else {
      setTheme("dark");
    }
  };

  return {
    theme,
    isDark,
    setTheme,
    toggleTheme,
  };
}

export default useTheme;
